import React from 'react';

import { DefaultPageProps } from '@/lib/types/shared/page.types';

import { Container, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import Image from '@/components/shared/Image';

import { Swiper, SwiperSlide } from 'swiper/react';
import CustomLink from './CustomLink';
import ButtonOne from './ButtonOne';
import { route } from '@/lib/navigationFunctions';
import { Cursor } from './icons';

const cases = [
    {
        image: {
            src: 'Cases/brinks-watersport_roz83c',
            alt: 'Brinks Watersport',
            width: 574,
            height: 546,
        },
        name: 'Brinks Watersport',
        type: 'Fullservice',
        url: 'https://brinkswatersport.nl'
    },
    {
        image: {
            src: 'Cases/go-climate_kamm3z',
            alt: 'GoClimate',
            width: 574,
            height: 546,
        },
        name: 'GoClimate',
        type: 'Rebranding',
        url: 'https://goclimate.nl/'
    },
    {
        image: {
            src: 'Cases/ney-eco_hrqzml',
            alt: 'Amazon Tours Manaus',
            width: 574,
            height: 546,
        },
        name: 'Amazon Tours Manaus',
        type: 'Rebranding & proces optimalisatie',
        url: 'https://amazontourmanaus.com/nl/'
    },
    {
        image: {
            src: 'Cases/centrum-pe_vzklmf',
            alt: 'Centrum PE',
            width: 574,
            height: 546,
        },
        name: 'Centrum PE',
        type: 'Rebranding - Platform development',
        url: 'https://centrum-pe.nl/'
    },
    {
        image: {
            src: 'Cases/rizz-design_wutzyt',
            alt: 'RiZZ Design',
            width: 574,
            height: 546,
        },
        name: 'RiZZ Design',
        type: 'Webshop en conversie optimalisatie',
        url: 'https://rizz.design/'
    },
    {
        image: {
            src: 'Cases/freshguard_znn8at',
            alt: 'Freshguard',
            width: 574,
            height: 546,
        },
        name: 'Freshguard',
        type: 'Branding en website',
        url: 'https://freshguard.nl/'
    },
    {
        image: {
            src: 'Cases/online-video-producties_w5bcrn',
            alt: 'Online Video Producties',
            width: 574,
            height: 546,
        },
        name: 'Online Video Producties',
        type: 'SEO & Website Verbeteringen',
        url: 'https://onlinevideoproducties.nl/'
    },
    {
        image: {
            src: 'Cases/de-wit_kplfp9',
            alt: 'De Wit Spuitwerk',
            width: 574,
            height: 546,
        },
        name: 'De Wit Spuitwerk',
        type: 'Rebranding',
        url: 'https://dewitspuitwerk.nl/'
    },
    {
        image: {
            src: 'Cases/camping-nieuwhof_hcpfcj',
            alt: 'Camping Nieuwhof',
            width: 574,
            height: 546,
        },
        name: 'Camping Nieuwhof',
        type: 'Rebranding en SEO',
        url: 'https://campingnieuwhof.nl/'
    },
    {
        image: {
            src: 'Cases/autogarage_wtzypl',
            alt: 'Autogarage',
            width: 574,
            height: 546,
        },
        name: 'Autogarage',
        type: 'API Integratie',
        url: ''
    },
    {
        image: {
            src: 'Cases/brl-registratie_ltnjdx',
            alt: 'BRL Registratie',
            width: 574,
            height: 546,
        },
        name: 'BRL Registratie',
        type: 'Webapplicatie',
        url: 'https://brlregistratie.nl/'
    },
    {
        image: {
            src: 'Cases/de-wijnles_d3tsta',
            alt: 'Wijnles E-learning',
            width: 574,
            height: 546,
        },
        name: 'Wijnles E-learning',
        type: 'Webapplicatie',
        url: 'https://dewijnles.nl/'
    },
];

type Props = {
    addButton?: boolean;
} & DefaultPageProps;

const CasesSection = ({ params, addButton = false }: Props) => {
    const t = useTranslations('shared.cases');

    return (
        <section id='cases' className="py-16 bg-white scroll-mt-16">
            <Container>
                <Typography variant="h2" component="h2" className="text-primary-gray-950 font-bold capitalize">
                    {t('title')}
                </Typography>

                <hr className='w-full border-primary-gray-950 mb-2' />

                <div>
                    <style>
                        {`
                            .swiper-wrapper {
                                display: flex!important;
                                flex-wrap: nowrap!important;
                            }
                        `}
                    </style>

                    <Swiper
                        spaceBetween={24}
                        slidesPerView={1}
                        breakpoints={{
                            425: {
                                slidesPerView: 1.25,
                            },
                            640: {
                                slidesPerView: 2.15,
                            },
                            1024: {
                                slidesPerView: 2.25,
                            },
                        }}
                        initialSlide={0}
                        loop={true}
                        style={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                        }}
                        className='my-8'
                    >
                        {cases.map((c, index) => (
                            <SwiperSlide key={index} className='h-fit p-0.5 rounded-3xl bg-primary-gray-950 aspect-square cursor-grab overflow-hidden relative'>
                                {/* Background image */}
                                <Image
                                    src={c.image.src}
                                    alt={c.image.alt}
                                    width={c.image.width}
                                    height={c.image.height}
                                    className='rounded-lg w-full h-full object-cover'
                                />

                                {/* Mask bottom left */}
                                <Image
                                    src="Masks/mask-bottom-left_e0lrdt"
                                    alt="Mask Bottom Left"
                                    width={445}
                                    height={261}
                                    className='rounded-lg w-4/5 h-auto absolute bottom-0 left-0'
                                />

                                {/* Text */}
                                <div className='absolute bottom-0 left-0 p-4'>
                                    <Typography variant="h4" component="h2" className="text-xl md:text-2xl text-white font-semibold capitalize">
                                        {c.type}
                                    </Typography>

                                    <Typography variant="h2" component="h3" className="md:text-4xl lg:text-5xl text-white font-bold capitalize">
                                        {c.name}
                                    </Typography>
                                </div>

                                {/* Link */}
                                <CustomLink ariaLabel='Link naar website' className='absolute bottom-0 right-0 m-5' variant='dark-border-white' iconColor='fill-primary-yellow-400' href={c.url} target='_blank' />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {addButton && (
                    <div className='flex justify-center'>
                        <ButtonOne paddingX='extra-small' customIcon={
                            <div className='bg-primary-yellow-t rounded-lg p-2'>
                                <Cursor color='fill-primary-yellow-400' />
                            </div>
                        } iconPos='left' variant='orange-300' text={t('button')} link={route('/cases', params.locale)} className='mr-0' />
                    </div>
                )}
            </Container>
        </section>
    );
}

export default CasesSection;